import { useContext, useEffect, useState } from 'react'
import { buildUserProfileSchema, ListNames } from '../config/configuration'
import { getTeamMemberPeopleSuggestions } from '../services/detailview'
import { UserDetailsContext } from '../utils/user-context'

const NOTIFICATION_PREF_SOURCE = 'notification-preferences'

export interface UserProfileItem {
	ID: number
	FirstName: string
	LastName: string
	PhoneNumber: string
	Email: string
}

const splitDisplayName = (fullName: string): { FirstName: string; LastName: string } => {
	const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean)
	if (parts.length === 0) {
		return { FirstName: '', LastName: '' }
	}
	return {
		FirstName: parts[0] ?? '',
		LastName: parts.slice(1).join(' ')
	}
}

interface UserNotificationPreferences {
	ID: number
	discussionAddCustomer: boolean
	discussionAddInternal: boolean
	replyDiscussion: boolean
	documentAdd: boolean
	documentDelete: boolean
	teamMemberAdd: boolean
	teamMemberDelete: boolean
	resolveInquiry: boolean
	cancelInquiry: boolean
	formUpdate: boolean
	userRole: string
}

const escapeODataValue = (value: string): string => value.replace(/'/g, '\'\'')

interface UserProfileFieldMap {
	userId: string
	phoneNumber?: string
	firstName?: string
	lastName?: string
	email?: string
	emailType?: string
}

let userProfileFieldMapPromise: Promise<UserProfileFieldMap> | null = null

const getUserProfileFieldMap = async (): Promise<UserProfileFieldMap> => {
	if (!userProfileFieldMapPromise) {
		userProfileFieldMapPromise = (async () => {
			const fields: any[] = await sp.web.lists
				.getByTitle(ListNames.USERPROFILE)
				.fields
				.select('Title', 'InternalName', 'TypeAsString')()

			const findField = (...names: string[]) =>
				fields.find((field: any) => names.includes(field.InternalName) || names.includes(field.Title))

			const userIdField = findField('UserID')

			if (!userIdField) {
				throw new Error('Required UserProfileList fields are missing.')
			}

			const phoneField = findField('PhoneNumber', 'DutyPhone')
			const firstNameField = findField('FirstName')
			const lastNameField = findField('LastName')
			const emailField = findField('Email')

			return {
				userId: userIdField.InternalName,
				phoneNumber: phoneField?.InternalName,
				firstName: firstNameField?.InternalName,
				lastName: lastNameField?.InternalName,
				email: emailField?.InternalName,
				emailType: emailField?.TypeAsString
			}
		})()
	}

	return userProfileFieldMapPromise
}

const findUserProfileItem = async (
	userEmail: string,
	loginName: string,
	displayName: string
): Promise<any | null> => {
	const candidates = [userEmail, loginName, displayName]
		.map((value) => (value ?? '').trim())
		.filter(Boolean)

	if (!candidates.length) return null

	try {
		const fieldMap = await getUserProfileFieldMap()
		const filter = candidates
			.flatMap((value) => [
				`${fieldMap.userId} eq '${escapeODataValue(value)}'`,
				`Title eq '${escapeODataValue(value)}'`
			])
			.join(' or ')

		const selectFields = ['ID', 'Title', fieldMap.userId, 'ItemModified']
		if (fieldMap.phoneNumber) selectFields.push(fieldMap.phoneNumber)
		if (fieldMap.firstName) selectFields.push(fieldMap.firstName)
		if (fieldMap.lastName) selectFields.push(fieldMap.lastName)
		if (fieldMap.email) {
			if (fieldMap.emailType === 'User') {
				selectFields.push(`${fieldMap.email}/EMail`)
			} else {
				selectFields.push(fieldMap.email)
			}
		}

		let query = sp.web.lists
			.getByTitle(ListNames.USERPROFILE)
			.items
			.select(selectFields.join(','))
			.filter(filter)
			.orderBy('ItemModified', false)

		if (fieldMap.email && fieldMap.emailType === 'User') {
			query = query.expand(fieldMap.email)
		}

		const items = await query()

		return items?.[0] ?? null
	} catch (error) {
		console.error('Failed to load user profile item:', error)
		return null
	}
}

export const getUserProfile = async (
	userEmail: string,
	loginName: string,
	displayName: string
): Promise<UserProfileItem | null> => {
	if (!userEmail && !loginName && !displayName) return null
	try {
		const fieldMap = await getUserProfileFieldMap()
		const item = await findUserProfileItem(userEmail, loginName, displayName)
		if (!item) return null
		const storedDisplayName = item.Title || ''
		const storedFirstName = fieldMap.firstName ? item[fieldMap.firstName] : null
		const storedLastName = fieldMap.lastName ? item[fieldMap.lastName] : null
		const storedPhoneNumber = fieldMap.phoneNumber ? item[fieldMap.phoneNumber] : null
		const storedEmail = fieldMap.email
			? fieldMap.emailType === 'User'
				? item[fieldMap.email]?.EMail || null
				: item[fieldMap.email] || null
			: null
		const { FirstName: derivedFirstName, LastName: derivedLastName } = splitDisplayName(storedDisplayName)
		return {
			ID: item.ID,
			FirstName: storedFirstName ?? derivedFirstName,
			LastName: storedLastName ?? derivedLastName,
			PhoneNumber: storedPhoneNumber ?? '',
			Email: storedEmail ?? item[fieldMap.userId] ?? ''
		}
	} catch (error) {
		console.error('Failed to load user profile:', error)
		return null
	}
}

export const saveUserProfile = async (data: any, existingId?: number): Promise<void> => {
	if (existingId) {
		await pnpUpdate(ListNames.USERPROFILE, { ID: existingId, ...data })
		return
	}
	await pnpAdd(ListNames.USERPROFILE, data)
}

export const getUserNotificationPreferences = async (loginName: string): Promise<UserNotificationPreferences | null> => {
	if (!loginName) return null
	try {
		const items = await sp.web.lists
			.getByTitle(ListNames.USERPREFERENCES)
			.items
			.select('ID', 'UserId', 'Source', 'SelectedColumns', 'userRole')
			.filter(`UserId eq '${escapeODataValue(loginName)}' and Source eq '${NOTIFICATION_PREF_SOURCE}'`)()
		const item = items?.[0]
		if (!item) return null
		const parsed = JSON.parse(item.SelectedColumns ?? '{}')
		return { ID: item.ID, ...parsed, userRole: item.userRole ?? '' }
	} catch (error) {
		console.error('Failed to load notification preferences:', error)
		return null
	}
}

export const saveUserNotificationPreferences = async (
	data: any,
	loginName: string,
	existingId?: number
): Promise<void> => {
	const { userRole, ...rest } = data
	const normalizedUserRole = typeof userRole === 'object' && userRole !== null
		? (userRole.value ?? userRole.label ?? '')
		: (userRole ?? '')
	const payload = {
		Title: `${NOTIFICATION_PREF_SOURCE}-${loginName}`,
		UserId: loginName,
		Source: NOTIFICATION_PREF_SOURCE,
		userRole: normalizedUserRole,
		SelectedColumns: JSON.stringify(rest)
	}
	if (existingId) {
		await pnpUpdate(ListNames.USERPREFERENCES, { ID: existingId, ...payload })
		return
	}
	await pnpAdd(ListNames.USERPREFERENCES, payload)
}
// ─── UI Config & Logic ───────────────────────────────────────────────────────

export const NOTIFICATION_FIELDS = new Set([
	'userRole', 'discussionAddCustomer', 'discussionAddInternal',
	'replyDiscussion', 'documentAdd', 'documentDelete', 'teamMemberAdd',
	'teamMemberDelete', 'resolveInquiry', 'cancelInquiry', 'formUpdate'
])

export const toFormData = (item: any): any => {
	const skip = new Set(['ID', 'Id', 'odata.etag', '__metadata', 'UserId', 'Source', 'SelectedColumns', 'ItemCreated', 'ItemModified', 'ItemCreatedById', 'ItemModifiedById'])
	return Object.fromEntries(Object.entries(item).filter(([k]) => !skip.has(k)))
}

export const buildProfilePayload = (
	data: any,
	userId: number,
	displayName: string,
	fieldMap: UserProfileFieldMap,
	profileId?: number,
	userEmail?: string,
): Record<string, any> => {
	const now = new Date().toISOString()
	const fullName = [data.FirstName ?? '', data.LastName ?? ''].join(' ').trim() || displayName || ''
	const emailValue = data.Email && typeof data.Email === 'object'
		? data.Email.email || data.Email.EMail || ''
		: (data.Email ?? '')
	return {
		Title: fullName,
		[fieldMap.userId]: userEmail ?? '',
		...(fieldMap.firstName && data.FirstName ? { [fieldMap.firstName]: data.FirstName } : {}),
		...(fieldMap.lastName && data.LastName ? { [fieldMap.lastName]: data.LastName } : {}),
		...(fieldMap.phoneNumber && data.PhoneNumber ? { [fieldMap.phoneNumber]: data.PhoneNumber } : {}),
		...(fieldMap.email && fieldMap.emailType !== 'User' && emailValue ? { [fieldMap.email]: emailValue } : {}),
		ItemModified: now,
		ItemModifiedById: userId,
		...(!profileId && {
			ItemCreated: now,
			ItemCreatedById: userId
		})
	}
}

export const buildNotificationPayload = (data: any): any => {
	const notifFields: any = {}
	Object.entries(data).forEach(([key, val]) => {
		if (NOTIFICATION_FIELDS.has(key)) notifFields[key] = val
	})
	return notifFields
}

// ─── Custom Hook ─────────────────────────────────────────────────────────────

export function useProfilePage() {
	const { pageContextdets } = useContext(UserDetailsContext)
	const [profileData, setProfileData] = useState<any>({})
	const [notifData, setNotifData] = useState<any>({})
	const [profileId, setProfileId] = useState<number | undefined>()
	const [notificationPreferenceId, setNotificationPreferenceId] = useState<number | undefined>()
	const [isEdit, setIsEdit] = useState(false)
	const [emailSuggestions, setEmailSuggestions] = useState<any[]>([])
	const [dataReady, setDataReady] = useState(false)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		const loginName: string = pageContextdets?.userLoginName ?? ''
		const userId: number = pageContextdets?.userId
		const userEmail: string = pageContextdets?.userEmail ?? ''
		const displayName: string = pageContextdets?.userDisplayName ?? ''
		if (!loginName || !userId) {
			setLoading(false)
			return
		}
		setLoading(true)
		Promise.all([
			getUserProfile(userEmail, loginName, displayName).then(async profileItem => {
				if (profileItem) {
					setProfileData(toFormData(profileItem))
					setProfileId(profileItem.ID)
					setIsEdit(true)
				} else {
					// Auto-create profile on first login using SP context data
					const { FirstName, LastName } = splitDisplayName(displayName)
					const autoData = { FirstName, LastName, Email: userEmail }
					try {
						const fieldMap = await getUserProfileFieldMap()
						const payload = buildProfilePayload(autoData, userId, displayName, fieldMap, undefined, userEmail)
						await saveUserProfile(payload)
						const created = await getUserProfile(userEmail, loginName, displayName)
						if (created) {
							setProfileData(toFormData(created))
							setProfileId(created.ID)
							setIsEdit(true)
						} else {
							setProfileData(autoData)
						}
					} catch {
						setProfileData(autoData)
					}
				}
			}),
			getUserNotificationPreferences(loginName).then(pref => {
				if (pref) {
					setNotifData(toFormData(pref))
					setNotificationPreferenceId(pref.ID)
				}
			})
		]).finally(() => {
			setDataReady(true)
			setLoading(false)
		})
	}, [pageContextdets])

	const handleChange = async (name: string, value: any) => {
		if (name.endsWith('__clearSuggestions')) {
			setEmailSuggestions([])
			return
		}
		if (NOTIFICATION_FIELDS.has(name)) {
			setNotifData((prev: any) => ({ ...prev, [name]: value }))
			return
		}
		if (name === 'Email') {
			// Sync typed text back so parent re-renders don't overwrite the input
			if (typeof value === 'string') {
				setProfileData((prev: any) => ({ ...prev, Email: value }))
			}
			if (!value) {
				setEmailSuggestions([])
				return
			}
			const suggestions = await getTeamMemberPeopleSuggestions(value)
			setEmailSuggestions(suggestions)
			return
		}
		setProfileData((prev: any) => ({ ...prev, [name]: value }))
	}

	const handleSelect = (name: string, value: any) => {
		if (name !== 'Email') return
		setProfileData((prev: any) => ({ ...prev, Email: value ?? '' }))
		setEmailSuggestions([])
	}

	const saveProfileData = async (data: any) => {
		const loginName: string = pageContextdets?.userLoginName ?? ''
		const userId: number = pageContextdets?.userId
		const userEmail: string = pageContextdets?.userEmail ?? ''
		const displayName: string = pageContextdets?.userDisplayName ?? ''
		if (!loginName || !userId) {
			throw new Error('User context not loaded. Please refresh and try again.')
		}
		setLoading(true)
		try {
			const fieldMap = await getUserProfileFieldMap()
			const matchedProfile = profileId
				? { ID: profileId }
				: await findUserProfileItem(userEmail, loginName, displayName)
			const payload = buildProfilePayload(data, userId, displayName, fieldMap, matchedProfile?.ID, userEmail)
			await saveUserProfile(payload, matchedProfile?.ID)
			const updated = await getUserProfile(userEmail, loginName, displayName)
			if (updated) {
				setProfileData(toFormData(updated))
				setProfileId(updated.ID)
				setIsEdit(true)
			}
		} finally {
			setLoading(false)
		}
	}

	const saveNotificationData = async (data: any) => {
		const loginName: string = pageContextdets?.userLoginName ?? ''
		const notifFields = buildNotificationPayload(data)
		setLoading(true)
		try {
			await saveUserNotificationPreferences(notifFields, loginName, notificationPreferenceId)
			const pref = await getUserNotificationPreferences(loginName)
			if (pref) {
				setNotifData(toFormData(pref))
				setNotificationPreferenceId(pref.ID)
			}
		} finally {
			setLoading(false)
		}
	}

	const handleProfileSubmit = async (data: any) => {
		try {
			await saveProfileData(data)
		} catch (err) {
			console.error('Profile save error:', err)
		}
	}

	const handleNotificationSubmit = async (data: any) => {
		try {
			await saveNotificationData(data)
		} catch (err) {
			console.error('Notification preference save error:', err)
		}
	}

	const isEditing = false

	return {
		data: { ...profileData, ...notifData },
		isEdit,
		isEditing,
		profileKey: dataReady ? 'profile-loaded' : 'loading',
		schema: buildUserProfileSchema(isEdit, isEditing),
		peopleSuggestions: { Email: emailSuggestions },
		handleChange,
		handleSelect,
		handleProfileSubmit,
		handleNotificationSubmit,
		loading,
	}
}
